import { ApolloClient, gql } from "@apollo/client";
import {
  CustomFieldEntity,
  CustomFieldFormValues,
  CustomFieldType,
  ExpenseAutofillInfo,
} from "@common/utils/graphql";

export const LIST_FIELD_TYPES = [
  CustomFieldType.List,
  CustomFieldType.MultiLevelList,
];

export const isListField = (customField: Pick<CustomFieldEntity, "type">) =>
  LIST_FIELD_TYPES.includes(customField.type);

const CustomFieldListOptionFragment = gql`
  fragment ListOption on CustomFieldListOption {
    uuid
  }
`;

const CustomFieldEntityFragment = gql`
  fragment CustomEntity on CustomFieldEntity {
    uuid
  }
`;

// Format form values for api payload
export const sanatizeCustomFieldEntries = (
  customFieldValues: CustomFieldFormValues,
) => {
  return Object.values(customFieldValues).map(
    ({
      type,
      customFieldEntityId,
      customFieldListOptionId,
      booleanValue,
      rawValue,
    }) => {
      if (isListField({ type })) {
        return {
          customFieldEntityId: Number(customFieldEntityId),
          customFieldListOptionId: customFieldListOptionId
            ? Number(customFieldListOptionId)
            : null,
        };
      } else if (type === CustomFieldType.Checkbox) {
        return {
          customFieldEntityId: Number(customFieldEntityId),
          booleanValue: !!booleanValue,
        };
      }
      return {
        customFieldEntityId: Number(customFieldEntityId),
        rawValue: rawValue ? String(rawValue) : null,
      };
    },
  );
};

// Get the parent field option uuid for a list field from the cache
export const getParentFieldOptionUuid = (
  client: ApolloClient<object>,
  parentFieldOptionId: number,
) => {
  const data: { uuid: string } | null = client.cache.readFragment({
    id: `CustomFieldListOption:${parentFieldOptionId}`,
    fragment: CustomFieldListOptionFragment,
  });
  return data?.uuid ?? null;
};

export const getCustomFieldEntityUuid = (
  client: ApolloClient<object>,
  customFieldEntityId: number,
) => {
  const data: { uuid: string } | null = client.cache.readFragment({
    id: `CustomFieldEntity:${customFieldEntityId}`,
    fragment: CustomFieldEntityFragment,
  });
  return data?.uuid ?? null;
};

export const sanatizeOCRCustomFieldsData = (
  client: ApolloClient<object>,
  customFields: ExpenseAutofillInfo["customFields"],
) => {
  return (
    customFields?.reduce(
      (
        acc,
        {
          customFieldEntityId,
          customFieldListOptionId,
          booleanValue,
          rawValue,
          customFieldEntityType,
        },
      ) => {
        const customFieldEntityUuid = getCustomFieldEntityUuid(
          client,
          customFieldEntityId,
        );
        if (!customFieldEntityUuid) {
          return acc;
        }
        switch (customFieldEntityType) {
          case CustomFieldType.List:
          case CustomFieldType.MultiLevelList:
            return {
              ...acc,
              [customFieldEntityUuid]: {
                customFieldEntityId,
                customFieldListOptionId,
                type: customFieldEntityType,
              },
            };
          case CustomFieldType.Checkbox:
            return {
              ...acc,
              [customFieldEntityUuid]: {
                customFieldEntityId,
                booleanValue,
                type: customFieldEntityType,
              },
            };
          case CustomFieldType.ShortText:
            return {
              ...acc,
              [customFieldEntityUuid]: {
                customFieldEntityId,
                rawValue,
                type: customFieldEntityType,
              },
            };
        }
      },
      {},
    ) ?? {}
  );
};

// Gets the child multi level field with matching parentEntityUuid
// or gets the first parent multi level field if parentEntityUuid is null
const getMultiLevelCustomField = (
  customFields: CustomFieldEntity[],
  parentEntityUuid: string | null,
) =>
  customFields.find(
    (customField) =>
      customField.type === CustomFieldType.MultiLevelList &&
      customField.parentEntityUuid === parentEntityUuid,
  );

export const isChildOfMultiLevelList = (customField: CustomFieldEntity) =>
  customField.type === CustomFieldType.MultiLevelList &&
  !!customField.parentEntityUuid;

// Sorts the custom fields so multi level lists are ordered correctly
export const orderCustomFields = (customFields: CustomFieldEntity[]) => {
  const orderedCustomFields = [];
  let customFieldsCopy = [...customFields];

  // find first parent of multi level list
  let multiLeverCustomField = getMultiLevelCustomField(customFieldsCopy, null);
  while (multiLeverCustomField !== undefined) {
    // remove from customFieldsCopy
    customFieldsCopy = customFieldsCopy.filter(
      (customField) => customField.uuid !== multiLeverCustomField?.uuid,
    );
    orderedCustomFields.push(multiLeverCustomField);

    // get the next child of the multi-level list
    multiLeverCustomField = getMultiLevelCustomField(
      customFieldsCopy,
      multiLeverCustomField.uuid,
    );

    // If no child found, check for custom field that is the first parent of a multi-level list
    if (multiLeverCustomField === undefined) {
      multiLeverCustomField = getMultiLevelCustomField(customFieldsCopy, null);
    }
  }
  return [...orderedCustomFields, ...customFieldsCopy];
};

export const getMultiLevelCustomFieldName = (uuid: string) =>
  `customFieldEntries.${uuid}.customFieldListOptionId`;

export const getCustomFormFieldName = (customField: CustomFieldEntity) => {
  if (isListField(customField)) {
    return getMultiLevelCustomFieldName(customField.uuid);
  } else if (customField.type === CustomFieldType.ShortText) {
    return `customFieldEntries.${customField.uuid}.rawValue`;
  } else {
    return `customFieldEntries.${customField.uuid}.booleanValue`;
  }
};
